import { html } from "./dom-utils";

export const PROJECT_VERSION_POLL_MS = 15000;

export const STALE_PROJECT_MESSAGE =
  "This project was updated by someone else or in another window. Reload the page to see the latest version. Unsaved changes on this page will be lost.";

/**
 * Polls the current version of a project and shows a banner inside `element` once
 * someone else saves a newer one. The element carries data-project-uri and
 * data-project-version, and must be empty: the banner is rendered into it. The version is
 * read on every check, so a page that re-renders it after its own save is not warned
 * about that save. Polling stops once the banner shows or the element leaves the page.
 */
export function watchProjectVersion(
  element: HTMLElement,
  intervalMs: number = PROJECT_VERSION_POLL_MS,
): void {
  const uri = element.dataset.projectUri;
  if (!uri) return;

  const timerId = window.setInterval(async () => {
    if (!element.isConnected) {
      window.clearInterval(timerId);
      return;
    }
    if (document.hidden) return;

    let currentId: number;
    try {
      const response = await fetch(
        `/api/project-version/${encodeURIComponent(uri)}`,
        { cache: "no-store" },
      );
      if (!response.ok) return;
      currentId = (await response.json()).projectId;
    } catch (error) {
      console.warn("Project version check failed", error);
      return;
    }

    if (currentId === Number(element.dataset.projectVersion)) return;

    window.clearInterval(timerId);
    element.replaceChildren(
      html`<div
        class="alert alert-warning d-flex justify-content-between align-items-center"
        role="alert"
      >
        <span>${STALE_PROJECT_MESSAGE}</span>
        <button
          type="button"
          class="btn btn-sm btn-outline-dark ml-3"
          onClick=${() => window.location.reload()}
        >
          Reload
        </button>
      </div>` as HTMLElement,
    );
  }, intervalMs);
}
